import { useSession } from '@/auth/useSession';
import { CopyableLink } from '@/components/copyable-link';
import { EditableInfoCard, SubmitProps } from '@/components/editable-info-card';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { FormTextArea } from '@/components/forms/form-text-area';
import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { LinkToExternal } from '@/components/link-to-external';
import { CancelSessionSubstituteByAdmin } from '@/components/session/cancel-session-substitue-by-admin';
import { CreateSubstitutionRequestByAdmin } from '@/components/session/create-substitution-request-by-admin';
import { ZoomNotStartedWarning } from '@/components/session/zoom-not-started-warning';
import { Tooltip } from '@/components/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FormLabel } from '@/components/ui/form';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  buildSessionRemarksV2Options,
  buildUserSelectOptions,
  comboBoxOptionsOf,
  emptySelectOption,
  multiSelectOptions,
  requiredSelectOption,
} from '@/components/ui/multi-select-utils';
import { coachesTypeaheadSearch, lessonTypeaheadSearch } from '@/lib/api-call-hooks';
import {
  createSessionFeedbacks,
  EmptyArray,
  getAdminSession,
  getAdminSessions,
  getSessionFeedbacks,
  getSessionsForStudentGroup,
  getStudentGroup,
  updateSessionDetails,
} from '@/lib/api-calls';
import { CoachesTypeaheadSearchOptions } from '@/lib/coach-utils';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import {
  getOriginalTeacherIssueDropdownText,
  getOriginalTeacherIssueOptionClassName,
  getOriginalTeacherIssueOptionHint,
  isSubRequestIssue,
  mapIssueFromSessionRemarks,
  orderedOriginalTeacherIssueOptions,
  shouldShowReasonField,
} from '@/lib/original-teacher-issue-utils';
import { validateRouteRole } from '@/lib/routing';
import { nullIfString, stringIfNull } from '@/lib/string-utils';
import { AllTimeZones, timezoneZodSchema } from '@/lib/time-zones';
import { toInputTimeValue } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { SessionStudentsTable } from '@/routes/_layout/sessions/-components/session-students-table';
import {
  formatInstant,
  FrequentTimeZones,
  getLearningLossLabel,
  getSessionFormattedSchedule,
  getSessionSubstitutionRequestSummary,
  InstantCompare,
  isAirAdmin,
  isZoomMeetingNotStarted,
  normalizeOriginalTeacherIssueReason,
  SessionStatusToLabel,
  getActualSessionTeacherCoach,
} from '@airreading/common';
import {
  DEPRECATED_allPagesModifier,
  CoachSessionRemarkV2,
  CoachStatus,
  formatCoachSessionRemarkV2Label,
  LearningLossLabelMap,
  learningLossSelectableSchema,
  learningLossSelectableValues,
  MeetingStatus,
  MeetingStatusLabelMap,
  OriginalTeacherIssueLabelMap,
  originalTeacherIssueSelectableSchema,
  SessionStatus,
  sessionStatusSchema,
  UpdateSessionOptions,
  VideoConferenceType,
  VideoConferenceTypeLabels,
  videoConferenceTypeSchema,
} from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, Info } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { OriginalTeacherDisplay } from './-components/original-teacher-display';
import { SessionTeacherWithEditTrigger } from './-components/edit-session-teacher-modal';
import { SessionTeacherDisplay } from './-components/session-teacher-display';
import {
  SubstituteTimeHistoryTooltip,
  SubstituteTimeLabelWithHistoryTooltip,
  SubstituteTimeValueDisplay,
} from './-components/substitute-time-history-tooltip';
import { RefreshZoomLinkButton } from './-components/refresh-zoom-link-button';
import { SessionCancelButton } from './-components/session-cancel-button';
import { SessionCompleteButton } from './-components/session-complete-button';
import { SessionCompletionStatusCell } from './-components/session-completion-status-cell';
import { SessionDeleteButton } from './-components/session-delete-button';
import { SessionStartButton } from './-components/session-start-button';
import { UpdateStudentsButton } from './-components/update-session-students-modal';

export const Route = createFileRoute('/_layout/sessions/$sessionId')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SessionPage,
});

const editMeetingInfoFormSchema = z.object({
  date: z.instanceof(Temporal.PlainDate),
  overrideTimeZone: timezoneZodSchema.optional(),
  startTime: z.string(),
  endTime: z.string(),
  status: sessionStatusSchema,
  lesson: multiSelectOptions,
  zoom: z
    .object({
      hostUrl: z.string().nullable().optional(),
      attendeeUrl: z.string().nullable().optional(),
      zoomUuid: z.string().nullable().optional(),
    })
    .optional(),
  googleMeet: z
    .object({
      hostUrl: z.string().nullable().optional(),
      attendeeUrl: z.string().nullable().optional(),
    })
    .optional(),
  videoConferenceType: videoConferenceTypeSchema,
});
type EditMeetingInfoForm = z.infer<typeof editMeetingInfoFormSchema>;

const editTeacherInfoFormSchema = z.object({
  originalCoach: requiredSelectOption(),
  substituteCoach: requiredSelectOption(),
  shouldDismissLateTag: z.boolean().default(false).optional(),
  shouldDismissEndEarlyTag: z.boolean().default(false).optional(),
  coachSessionRemarksV2: multiSelectOptions,
  originalTeacherIssue: originalTeacherIssueSelectableSchema,
  reason: z.string().optional(),
  learningLoss: learningLossSelectableSchema,
});
type EditTeacherInfoForm = z.infer<typeof editTeacherInfoFormSchema>;

const SESSION_DETAIL = 'session-detail';

function sessionDetailValueTestId(section: string, field: string) {
  return `${SESSION_DETAIL}-${section}-value-${field}`;
}

function sessionDetailButtonTestId(action: string) {
  return `${SESSION_DETAIL}-button-${action}`;
}

function sessionDetailMenuItemTestId(action: string) {
  return `${SESSION_DETAIL}-menu-item-${action}`;
}

function SyncOriginalTeacherIssueFromSessionRemarks({ form }: { form: UseFormReturn<EditTeacherInfoForm> }) {
  const coachSessionRemarksV2 = useWatch({
    control: form.control,
    name: 'coachSessionRemarksV2',
  });
  const originalTeacherIssue = useWatch({
    control: form.control,
    name: 'originalTeacherIssue',
  });

  const mappedIssue = useMemo(
    () => mapIssueFromSessionRemarks(coachSessionRemarksV2.map((option) => option.value as CoachSessionRemarkV2)),
    [coachSessionRemarksV2],
  );
  const hadTriggerRef = useRef(mappedIssue !== 'null');

  useEffect(() => {
    if (mappedIssue !== 'null') {
      // Auto-populate only once when the field is empty.
      if (originalTeacherIssue !== 'null') {
        hadTriggerRef.current = true;
        return;
      }

      form.setValue('originalTeacherIssue', mappedIssue, {
        shouldDirty: true,
        shouldValidate: true,
      });
      hadTriggerRef.current = true;
      return;
    }

    const hadTriggerBefore = hadTriggerRef.current;
    hadTriggerRef.current = false;

    // Only clear after a real trigger removal transition.
    if (!hadTriggerBefore) {
      return;
    }

    // Clear any sub-request issue once sub-class triggers are removed from remarks.
    if (!isSubRequestIssue(originalTeacherIssue)) {
      return;
    }

    form.setValue('originalTeacherIssue', 'null', {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [form, mappedIssue, originalTeacherIssue]);

  return null;
}

function SessionPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load session. Please try again.',
          back: {
            backLink: {
              to: '/sessions',
            },
          },
        };
      }}
    >
      <SessionDetail />
    </InfoPageSuspenseBoundary>
  );
}

function SessionDetail() {
  const { actingUser } = useSession();
  const { sessionId } = Route.useParams();
  const search = Route.useSearch();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const from = (search as { from?: string }).from;

  const getBackLink = () => {
    switch (from) {
      case 'past':
        return '/sessions/past';
      case 'future':
        return '/sessions/future';
      default:
        return '/sessions';
    }
  };
  const [{ data: session, error }] = useSuspenseQueries({
    queries: [getAdminSession(sessionId)],
  });

  const { data: studentGroup } = useSuspenseQuery(getStudentGroup(session.studentGroupId));
  const { data: sessionFeedbacksResp, isPending: isLoadingSessionFeedbacks } = useQuery(
    getSessionFeedbacks({
      filters: {
        sessionId,
      },
      ...DEPRECATED_allPagesModifier,
    }),
  );
  const orgTimeZone = studentGroup.classTime.schedule.school.org.timeZone;
  const displayTimeZone = FrequentTimeZones.UsCentral;
  const { startInstant, endInstant, coach: originalCoach } = session;
  const actualSessionTeacher = getActualSessionTeacherCoach(session);
  const zoomRecordingUrls = session.zoom?.recordingUrls ?? [];

  const {
    mostRecentNonCanceledSubRequest,
    substituteDateWithoutYearAndSeparator,
    coachIdWhoIsTeaching,
    coachIdToCreateNewSubRequest,
    subRequestToCancel,
    requestReason,
  } = useMemo(() => {
    const { mostRecentRequest, firstNonCanceledOriginalTeacherRequest, coachIdWhoIsTeaching } =
      getSessionSubstitutionRequestSummary(session);
    const hasPendingSubRequest = mostRecentRequest?.status === 'pending';
    const adminCanOpenNewSubRequest =
      (session.status === SessionStatus.scheduled || session.status === SessionStatus.in_progress) &&
      !hasPendingSubRequest &&
      // can only request sub before session end time
      InstantCompare(Temporal.Now.instant()).isBeforeOrEqual(session.endInstant) &&
      !!coachIdWhoIsTeaching;
    const subRequestToCancel = mostRecentRequest?.status === 'pending' ? mostRecentRequest : undefined;
    const coachIdToCreateNewSubRequest = adminCanOpenNewSubRequest ? coachIdWhoIsTeaching : undefined;
    const { formattedDateWithoutYearAndSeparator: substituteDateWithoutYearAndSeparator } =
      firstNonCanceledOriginalTeacherRequest?.createdAtInstant
        ? formatInstant(firstNonCanceledOriginalTeacherRequest.createdAtInstant, displayTimeZone)
        : {};

    return {
      mostRecentNonCanceledSubRequest: mostRecentRequest,
      substituteDateWithoutYearAndSeparator,
      coachIdWhoIsTeaching,
      coachIdToCreateNewSubRequest,
      subRequestToCancel,
      requestReason: firstNonCanceledOriginalTeacherRequest?.requestReason,
    };
  }, [session, displayTimeZone]);

  const sessionFeedbacks = useMemo(
    () =>
      // API should've deleted feedbacks from non-teaching coaches. But to be safe, we filter them out here
      (sessionFeedbacksResp?.sessionFeedbacks || EmptyArray).filter(({ coachId }) => coachId === coachIdWhoIsTeaching),
    [coachIdWhoIsTeaching, sessionFeedbacksResp?.sessionFeedbacks],
  );

  const { mutateAsync: updateSessionDetailsMutate } = useMutation({
    mutationFn: updateSessionDetails,
  });

  const { mutateAsync: createSessionFeedbacksMutate, isPending: isCreatingSessionFeedbacks } = useMutation({
    mutationFn: createSessionFeedbacks,
  });

  const didCatchError = useCallback(
    (title: string, e: unknown) => {
      const error = airServerErrorFrom(e);
      if (error) {
        toast({
          title,
          description: error.userFacingMessage,
          variant: 'destructive',
        });
      }
    },
    [toast],
  );

  const onSubmitMeetingInfo = ({ formData, disableEditing }: SubmitProps<EditMeetingInfoForm>) => {
    const { date, startTime, endTime, overrideTimeZone, lesson, videoConferenceType: vcType, ...rest } = formData;

    const startPlainTime = Temporal.PlainTime.from(startTime);
    const endPlainTime = Temporal.PlainTime.from(endTime);
    if (Temporal.PlainTime.compare(startPlainTime, endPlainTime) >= 0) {
      toast({
        title: 'Invalid time',
        description: 'Please enter a valid time range. The end time must be after the start time.',
      });
      return;
    }

    const sessionTimeZone = overrideTimeZone?.value || displayTimeZone;
    if (!sessionTimeZone) {
      toast({
        title: 'Invalid time zone',
        description: `The session's district does not have a time zone and you did not provide an override time zone. Either update the district to have a time zone or provide an override time zone.`,
      });
      return;
    }

    const updatedLesson = lesson.at(0);
    if (!updatedLesson || !updatedLesson.value) {
      toast({
        title: 'Invalid lesson',
        description: 'Please select a valid lesson for the session.',
      });
      return;
    }

    const start = date.toZonedDateTime({ plainTime: startPlainTime, timeZone: sessionTimeZone });
    const end = date.toZonedDateTime({ plainTime: endPlainTime, timeZone: sessionTimeZone });

    const updateData: UpdateSessionOptions = {
      ...rest,
      videoConferenceType: vcType,
      start: start.toInstant().toString(),
      end: end.toInstant().toString(),
      lessonId: updatedLesson.value,
      googleMeet: {
        hostUrl: formData.googleMeet?.hostUrl,
        attendeeUrl: formData.googleMeet?.hostUrl, // Overwrite attendee URL with host URL
      },
    };
    return updateSessionDetailsMutate({
      id: sessionId,
      data: updateData,
    })
      .then(() => {
        toast({ title: 'Session meeting information updated successfully' });
        disableEditing();
        queryClient.invalidateQueries(getAdminSession(sessionId));
        queryClient.invalidateQueries(getAdminSessions());
        queryClient.invalidateQueries(getSessionsForStudentGroup(session.studentGroupId));
        queryClient.invalidateQueries(getSessionFeedbacks());
      })
      .catch((e) => didCatchError('Session meeting information update failed', e));
  };

  const onSubmitTeacherInfo = async (submitProps: SubmitProps<EditTeacherInfoForm>) => {
    const { formData, disableEditing } = submitProps;
    const { originalTeacherIssue, reason, coachSessionRemarksV2, learningLoss, ...others } = formData;

    const metadataPayload: UpdateSessionOptions = {
      // updateSessionDetails currently requires status; preserve the existing value.
      status: session.status,
      // A metadata-only save must not send a class update email.
      shouldSendEmail: false,
      shouldDismissLateTag: others.shouldDismissLateTag,
      shouldDismissEndEarlyTag: others.shouldDismissEndEarlyTag,
      coachSessionRemarksV2: coachSessionRemarksV2.map((option) => option.value as CoachSessionRemarkV2),
      originalTeacherIssue: nullIfString(originalTeacherIssue),
      originalTeacherIssueReason: shouldShowReasonField(originalTeacherIssue)
        ? normalizeOriginalTeacherIssueReason(reason)
        : null,
      learningLoss: nullIfString(learningLoss),
    };

    return updateSessionDetailsMutate({
      id: sessionId,
      data: metadataPayload,
    })
      .then(() => {
        toast({ title: 'Session teacher information updated successfully' });
        disableEditing();
        queryClient.invalidateQueries(getAdminSession(sessionId));
        queryClient.invalidateQueries(getAdminSessions());
        queryClient.invalidateQueries(getSessionsForStudentGroup(session.studentGroupId));
        queryClient.invalidateQueries(getSessionFeedbacks());
      })
      .catch((e) => didCatchError('Session teacher information update failed', e));
  };

  if (error) {
    toast({ title: 'Error while fetching session' });
    return <div>Failed to fetch session</div>;
  }

  const showStartSessionButton =
    session.status === SessionStatus.scheduled &&
    Temporal.Instant.compare(startInstant, Temporal.Now.instant()) <= 0 &&
    Temporal.Instant.compare(endInstant, Temporal.Now.instant()) >= 0;
  const showEndSessionButton = session.status === SessionStatus.in_progress;
  const hasSessionFeedbacks = session.sessionFeedbacks.length === session.students.length;
  const sessionDate = session.startInstant.toZonedDateTimeISO(FrequentTimeZones.UsCentral).toPlainDate();
  const todayDate = Temporal.Now.plainDateISO(FrequentTimeZones.UsCentral);
  const isSessionTodayOrInPast = Temporal.PlainDate.compare(sessionDate, todayDate) <= 0;
  const showGenerateStudentFeedbackAction = !hasSessionFeedbacks && isSessionTodayOrInPast;

  const coachFilters: CoachesTypeaheadSearchOptions = {
    filters: {
      statuses: [CoachStatus.ready_to_teach],
    },
  };

  return (
    <div className="mb-2">
      <InfoPage
        headerText="Session Details"
        data-testid={SESSION_DETAIL}
        headerActions={
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                className="ml-auto h-8 flex"
                variant="secondary"
                data-testid={sessionDetailButtonTestId('actions')}
              >
                Actions
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              {showStartSessionButton && (
                <SessionStartButton
                  session={session}
                  trigger={
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      data-testid={sessionDetailMenuItemTestId('start-session')}
                    >
                      Start Session
                    </DropdownMenuItem>
                  }
                />
              )}
              {showEndSessionButton && (
                <SessionCompleteButton
                  session={session}
                  trigger={
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      data-testid={sessionDetailMenuItemTestId('complete-session')}
                    >
                      Complete Session
                    </DropdownMenuItem>
                  }
                />
              )}
              {coachIdToCreateNewSubRequest && (
                <CreateSubstitutionRequestByAdmin
                  session={session}
                  coachIdToCreateSubRequestFor={coachIdToCreateNewSubRequest}
                  trigger={
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      data-testid={sessionDetailMenuItemTestId('create-sub-request')}
                    >
                      Create Sub Request
                    </DropdownMenuItem>
                  }
                />
              )}
              {subRequestToCancel && (
                <CancelSessionSubstituteByAdmin
                  session={session}
                  subRequest={subRequestToCancel}
                  trigger={
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      data-testid={sessionDetailMenuItemTestId('cancel-sub-request')}
                    >
                      Cancel Sub Request
                    </DropdownMenuItem>
                  }
                />
              )}
              {session.videoConferenceType !== VideoConferenceType.googleMeet && (
                <RefreshZoomLinkButton
                  session={session}
                  trigger={
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      data-testid={sessionDetailMenuItemTestId('refresh-zoom-link')}
                    >
                      Refresh Zoom Link
                    </DropdownMenuItem>
                  }
                />
              )}
              <UpdateStudentsButton
                session={session}
                trigger={
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    data-testid={sessionDetailMenuItemTestId('update-students')}
                  >
                    Update Students
                  </DropdownMenuItem>
                }
              />
              {showGenerateStudentFeedbackAction && (
                <DropdownMenuItem
                  disabled={isCreatingSessionFeedbacks}
                  data-testid={sessionDetailMenuItemTestId('generate-student-feedback')}
                  onSelect={(e) => {
                    e.preventDefault();
                    createSessionFeedbacksMutate(session.id)
                      .then(() => {
                        toast({
                          title: 'Student feedback generated successfully',
                        });
                        queryClient.invalidateQueries(getAdminSessions());
                        queryClient.invalidateQueries(getAdminSession(sessionId));
                        queryClient.invalidateQueries(getSessionsForStudentGroup(session.studentGroupId));
                        queryClient.invalidateQueries(getSessionFeedbacks());
                      })
                      .catch((err) => {
                        const airServerError = airServerErrorFrom(err);
                        toast({
                          title: 'Failed to generate student feedback',
                          description: airServerError?.userFacingMessage,
                          variant: 'destructive',
                        });
                      });
                  }}
                >
                  {isCreatingSessionFeedbacks ? 'Generating student feedback...' : 'Generate student feedback'}
                </DropdownMenuItem>
              )}
              <SessionCancelButton
                session={session}
                trigger={
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    data-testid={sessionDetailMenuItemTestId('cancel-session')}
                  >
                    Cancel Session
                  </DropdownMenuItem>
                }
              />
              <SessionDeleteButton
                session={session}
                from={from}
                schoolId={session?.school?.id}
                trigger={
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    data-testid={sessionDetailMenuItemTestId('delete-session')}
                  >
                    Delete Session
                  </DropdownMenuItem>
                }
              />
            </DropdownMenuContent>
          </DropdownMenu>
        }
        breadcrumbs={{
          linkItems: [
            {
              label: 'Sessions',
              link: {
                to: getBackLink(),
              },
            },
          ],
          currentPage: 'Session Details',
        }}
      >
        <EditableInfoCard
          headerText="Meeting information"
          data-testid={`${SESSION_DETAIL}-meeting`}
          formSchema={editMeetingInfoFormSchema}
          defaultValues={{
            date: startInstant.toZonedDateTimeISO(displayTimeZone).toPlainDate(),
            startTime: toInputTimeValue(startInstant.toZonedDateTimeISO(displayTimeZone).toPlainTime()),
            endTime: toInputTimeValue(endInstant.toZonedDateTimeISO(displayTimeZone).toPlainTime()),
            status: session.status,
            lesson: [{ value: session.lesson.id, label: session.lesson.name }],
            zoom: {
              hostUrl: session.zoom?.hostUrl,
              attendeeUrl: session.zoom?.attendeeUrl,
              zoomUuid: session.zoom?.zoomUuid,
            },
            googleMeet: {
              hostUrl: session.googleMeet?.hostUrl,
              attendeeUrl: session.googleMeet?.attendeeUrl,
            },
            videoConferenceType: session.videoConferenceType,
          }}
          readonly={!isAirAdmin(actingUser)}
          onSubmit={onSubmitMeetingInfo}
        >
          {({ editing, form }) => {
            const selectionTimeZone = form.watch('overrideTimeZone') || displayTimeZone;
            const videoConferenceType = editing ? form.watch('videoConferenceType') : session.videoConferenceType;
            return (
              <>
                <InfoKeyValue
                  label="Date"
                  value={
                    <span data-testid={sessionDetailValueTestId('meeting', 'date')}>
                      {formatInstant(startInstant, displayTimeZone).formattedDate}
                    </span>
                  }
                  edit={{
                    editing,
                    editModeComponent: <FormInputDate name="date" label="Date" form={form} />,
                  }}
                />
                <InfoKeyValue
                  label="Scheduled time"
                  value={
                    <span data-testid={sessionDetailValueTestId('meeting', 'scheduled-time')}>
                      {`${getSessionFormattedSchedule({ startInstant, endInstant, displayTimeZone }).sessionTimeFormatted} (${displayTimeZone})`}
                    </span>
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormComboBox
                        name="overrideTimeZone"
                        label={
                          <div className="flex items-center gap-2">
                            <p className="font-bold">Schedule override time zone</p>
                            <Tooltip trigger={<Info className="h-4 w-4" />}>
                              The date/time you selected uses the student group's district's time zone ({orgTimeZone})
                              by default. You can override this if needed.
                            </Tooltip>
                          </div>
                        }
                        form={form}
                        triggerProps={{ className: 'w-[80%]' }}
                        {...comboBoxOptionsOf(AllTimeZones)}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Meeting status"
                  value={
                    session.meeting?.status ? (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-1 leading-none">
                          <span data-testid={sessionDetailValueTestId('meeting', 'meeting-status')}>
                            {MeetingStatusLabelMap[session.meeting.status]}
                          </span>
                          {isZoomMeetingNotStarted(session.meeting) && (
                            <ZoomNotStartedWarning
                              data-testid={sessionDetailValueTestId('meeting', 'zoom-not-started-warning')}
                            />
                          )}
                        </div>
                        {session.meeting.status === MeetingStatus.canceled && session.canceledAtInstant && (
                          <span className="text-xs text-gray-500">
                            Canceled at{' '}
                            {
                              formatInstant(session.canceledAtInstant, displayTimeZone, undefined, ', ')
                                .formattedDateTime
                            }
                          </span>
                        )}
                      </div>
                    ) : (
                      <span data-testid={sessionDetailValueTestId('meeting', 'meeting-status')}>
                        {SessionStatusToLabel[session.status]}
                      </span>
                    )
                  }
                />
                <InfoKeyValue
                  label="Recording links"
                  value={
                    zoomRecordingUrls.length > 0 ? (
                      <>
                        {zoomRecordingUrls.map((url, index) => (
                          <LinkToExternal
                            key={`${url}-${index}`}
                            href={url}
                            className="text-blue-600 hover:text-blue-800 underline block"
                            data-testid={sessionDetailValueTestId('meeting', `recording-link-${index + 1}`)}
                          >
                            {zoomRecordingUrls.length > 1 ? `Recording ${index + 1}` : 'View recording'}
                          </LinkToExternal>
                        ))}
                      </>
                    ) : (
                      <span data-testid={sessionDetailValueTestId('meeting', 'recording-links')}>-</span>
                    )
                  }
                />
                {editing ? (
                  <>
                    <FormInputField
                      form={form}
                      name="startTime"
                      label={`Schedule start time (${selectionTimeZone})`}
                      inputProps={{ type: 'time' }}
                    />
                    <FormInputField
                      form={form}
                      name="endTime"
                      label={`Schedule end time (${selectionTimeZone})`}
                      inputProps={{ type: 'time' }}
                    />
                  </>
                ) : (
                  <>
                    <InfoKeyValue
                      label="Actual start time"
                      value={
                        <span data-testid={sessionDetailValueTestId('meeting', 'actual-start-time')}>
                          {session.meeting?.startInstant
                            ? formatInstant(session.meeting.startInstant, displayTimeZone).formattedTime
                            : '-'}
                        </span>
                      }
                    />
                    <InfoKeyValue
                      label="Actual end time"
                      value={
                        <span data-testid={sessionDetailValueTestId('meeting', 'actual-end-time')}>
                          {session.meeting?.endInstant
                            ? formatInstant(session.meeting.endInstant, displayTimeZone).formattedTime
                            : '-'}
                        </span>
                      }
                    />
                  </>
                )}
                <InfoKeyValue
                  label="Lesson"
                  value={
                    <CopyableLink
                      to="/lessons/$lessonId"
                      params={{ lessonId: session.lesson.id }}
                      data-testid={sessionDetailValueTestId('meeting', 'link-lesson')}
                      copyButtonTestId={sessionDetailButtonTestId('copy-lesson-link')}
                    >
                      {session.lesson.name}
                    </CopyableLink>
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormMultiSelect
                        form={form}
                        label="Lesson"
                        name="lesson"
                        multipleSelectorProps={{
                          maxSelected: 1,
                          onSearch: lessonTypeaheadSearch(queryClient),
                          hideClearAllButton: true,
                        }}
                        className="w-[80%]"
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Meeting type"
                  value={
                    <span data-testid={sessionDetailValueTestId('meeting', 'meeting-type')}>
                      {session.videoConferenceType ? VideoConferenceTypeLabels[session.videoConferenceType] : '-'}
                    </span>
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormSingleSelect
                        form={form}
                        name="videoConferenceType"
                        label="Meeting type"
                        options={videoConferenceTypeSchema.options}
                        renderOption={(option) => VideoConferenceTypeLabels[option]}
                        triggerProps={{ className: 'w-[80%]' }}
                      />
                    ),
                  }}
                />
                {videoConferenceType === VideoConferenceType.zoom && (
                  <>
                    <InfoKeyValue
                      label="Zoom link"
                      value={
                        <CopyableLink
                          externalHref={session.zoom?.attendeeUrl ?? ''}
                          whenUrlEmptyRender={
                            !session.coachId
                              ? 'No teacher assigned to this session. Attendee URL will be auto-populated when a teacher is assigned'
                              : 'Not set yet. You can set it manually'
                          }
                          data-testid={sessionDetailValueTestId('meeting', 'zoom-link')}
                          copyButtonTestId={sessionDetailButtonTestId('copy-zoom-link')}
                        >
                          {session.zoom?.attendeeUrl ?? 'Join class'}
                        </CopyableLink>
                      }
                      edit={{
                        editing,
                        editModeComponent: (
                          <FormInputField
                            form={form}
                            name="zoom.attendeeUrl"
                            label="Zoom link"
                            inputProps={{ type: 'url' }}
                          />
                        ),
                      }}
                    />
                    <InfoKeyValue
                      label="Zoom ID"
                      value={
                        <span data-testid={sessionDetailValueTestId('meeting', 'zoom-id')}>
                          {session.zoom?.zoomUuid ?? '-'}
                        </span>
                      }
                    />
                    <InfoKeyValue
                      label="Zoom host URL"
                      value={
                        <CopyableLink
                          externalHref={session.zoom?.hostUrl ?? ''}
                          whenUrlEmptyRender="Host URL not set yet. This will be auto-populated 2hrs before session starts, or you can set it manually"
                          data-testid={sessionDetailValueTestId('meeting', 'zoom-host-link')}
                          copyButtonTestId={sessionDetailButtonTestId('copy-zoom-host-link')}
                        >
                          Start class
                        </CopyableLink>
                      }
                      edit={{
                        editing,
                        editModeComponent: (
                          <FormInputField
                            form={form}
                            name="zoom.hostUrl"
                            label="Zoom host URL"
                            inputProps={{ type: 'url' }}
                          />
                        ),
                      }}
                    />
                  </>
                )}
                {videoConferenceType === VideoConferenceType.googleMeet && (
                  <InfoKeyValue
                    label="Google Meet URL"
                    value={
                      <CopyableLink
                        externalHref={session.googleMeet?.hostUrl ?? session.googleMeet?.attendeeUrl ?? ''}
                        whenUrlEmptyRender={
                          !session.coachId
                            ? 'No teacher assigned to this session. Attendee URL will be auto-populated when a teacher is assigned'
                            : 'Not set yet. You can set it manually'
                        }
                        data-testid={sessionDetailValueTestId('meeting', 'google-meet-link')}
                        copyButtonTestId={sessionDetailButtonTestId('copy-google-meet-link')}
                      >
                        {session.googleMeet?.hostUrl ?? session.googleMeet?.attendeeUrl ?? 'Join class'}
                      </CopyableLink>
                    }
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormInputField
                          form={form}
                          name="googleMeet.hostUrl"
                          label="Google Meet URL"
                          inputProps={{ type: 'url' }}
                        />
                      ),
                    }}
                  />
                )}
              </>
            );
          }}
        </EditableInfoCard>
        <EditableInfoCard
          key={`teacher-${session.id}-${JSON.stringify(session.coachSessionRemarksV2)}`}
          headerText="Teacher"
          data-testid={`${SESSION_DETAIL}-teacher`}
          formSchema={editTeacherInfoFormSchema}
          defaultValues={{
            originalCoach: originalCoach ? buildUserSelectOptions(originalCoach) : emptySelectOption,
            substituteCoach: actualSessionTeacher ? buildUserSelectOptions(actualSessionTeacher) : emptySelectOption,
            shouldDismissLateTag: !!session.shouldDismissLateTag,
            shouldDismissEndEarlyTag: !!session.shouldDismissEndEarlyTag,
            coachSessionRemarksV2: buildSessionRemarksV2Options({ options: session.coachSessionRemarksV2 }),
            originalTeacherIssue: stringIfNull(session.originalTeacherIssue),
            reason: session.originalTeacherIssueReason ?? '',
            learningLoss: stringIfNull(session.learningLoss),
          }}
          readonly={!isAirAdmin(actingUser)}
          onSubmit={onSubmitTeacherInfo}
        >
          {({ editing, form }) => {
            const coachSessionRemarkV2 = form
              .watch('coachSessionRemarksV2')
              .map((t) => t.value as CoachSessionRemarkV2);
            const watchedOriginalTeacherIssue = form.watch('originalTeacherIssue');
            const showReasonField = shouldShowReasonField(watchedOriginalTeacherIssue);
            const sessionTeacherLabel = (
              <span className="inline-flex items-center gap-1">
                <span>Session teacher</span>
                <SessionTeacherWithEditTrigger
                  session={session}
                  showSessionTeacherName={false}
                  compactIcon
                  triggerTestId={sessionDetailButtonTestId('edit-session-teacher')}
                />
              </span>
            );

            return (
              <>
                {editing && <SyncOriginalTeacherIssueFromSessionRemarks form={form} />}
                <InfoKeyValue
                  label={sessionTeacherLabel}
                  value={
                    <SessionTeacherDisplay
                      session={session}
                      testId={sessionDetailValueTestId('teacher', 'session-teacher')}
                    />
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormComboBox
                        form={form}
                        label={sessionTeacherLabel}
                        name="substituteCoach"
                        placeholder="Select session teacher"
                        {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient, coachFilters))}
                        triggerProps={{
                          className: 'w-2/3',
                          disabled: true,
                        }}
                        triggerSearchOnFocus
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Original teacher"
                  value={
                    <OriginalTeacherDisplay
                      session={session}
                      testId={sessionDetailValueTestId('teacher', 'original-teacher')}
                    />
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormComboBox
                        form={form}
                        label="Original teacher"
                        name="originalCoach"
                        placeholder="Select original teacher"
                        {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient, coachFilters))}
                        triggerProps={{
                          className: 'w-2/3',
                          disabled: true,
                        }}
                        triggerSearchOnFocus
                      />
                    ),
                  }}
                />
                {mostRecentNonCanceledSubRequest ? (
                  <InfoKeyValue
                    label={
                      <div className="flex items-center gap-2">
                        <span>Substitute time</span>
                        <SubstituteTimeHistoryTooltip substitutionRequests={session.substitutionRequests} />
                      </div>
                    }
                    value={
                      <SubstituteTimeValueDisplay
                        request={mostRecentNonCanceledSubRequest}
                        testId={sessionDetailValueTestId('teacher', 'substitute-time')}
                      />
                    }
                    edit={{
                      editing,
                      editModeComponent: (
                        <div className="space-y-2 flex flex-col gap-2 mt-1">
                          <SubstituteTimeLabelWithHistoryTooltip substitutionRequests={session.substitutionRequests} />
                          <SubstituteTimeValueDisplay request={mostRecentNonCanceledSubRequest} />
                        </div>
                      ),
                    }}
                  />
                ) : null}
                <InfoKeyValue
                  label="Original teacher issue"
                  value={
                    session.originalTeacherIssue ? (
                      <div
                        className="flex flex-col gap-1"
                        data-testid={sessionDetailValueTestId('teacher', 'original-teacher-issue')}
                      >
                        <span>{OriginalTeacherIssueLabelMap[session.originalTeacherIssue]}</span>
                        {isAirAdmin(actingUser) && shouldShowReasonField(session.originalTeacherIssue) ? (
                          <div className="flex flex-col gap-1">
                            <span className="font-medium">Reason:</span>
                            <span className="whitespace-pre-wrap break-words line-clamp-3">
                              Submitted by teacher on {substituteDateWithoutYearAndSeparator}:{' '}
                              {requestReason?.trim() ? requestReason : 'No reason provided'}
                            </span>
                            {session.originalTeacherIssueReason?.trim() &&
                              session.originalTeacherIssueReasonDateInstant && (
                                <span className="whitespace-pre-wrap break-words line-clamp-3">
                                  Updated by admin on{' '}
                                  {
                                    formatInstant(session.originalTeacherIssueReasonDateInstant, displayTimeZone)
                                      .formattedDateWithoutYearAndSeparator
                                  }
                                  : {session.originalTeacherIssueReason}
                                </span>
                              )}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span data-testid={sessionDetailValueTestId('teacher', 'original-teacher-issue')}>-</span>
                    )
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <div className="space-y-2">
                        <FormSingleSelect
                          key={`originalTeacherIssue-${watchedOriginalTeacherIssue}`}
                          name="originalTeacherIssue"
                          label="Original teacher issue"
                          form={form}
                          options={orderedOriginalTeacherIssueOptions}
                          renderOption={(issue) => {
                            const optionHint = getOriginalTeacherIssueOptionHint(issue);
                            return (
                              <div>
                                <div>{getOriginalTeacherIssueDropdownText(issue)}</div>
                                {optionHint ? <div className="text-muted-foreground">{optionHint}</div> : null}
                              </div>
                            );
                          }}
                          renderOptionSelected={getOriginalTeacherIssueDropdownText}
                          optionClassName={getOriginalTeacherIssueOptionClassName}
                          triggerProps={{ className: 'w-2/3 [&>span]:block [&>span]:text-left' }}
                        />
                        {showReasonField ? (
                          <FormTextArea
                            name="reason"
                            label="Reason"
                            form={form}
                            textAreaProps={{
                              className: 'w-2/3 resize-none max-h-24',
                              placeholder: 'Please enter a reason',
                              rows: 3,
                            }}
                          />
                        ) : null}
                      </div>
                    ),
                  }}
                />
                <InfoKeyValue
                  label={
                    <div className="flex items-center gap-2">
                      <span>Session remarks</span>
                    </div>
                  }
                  value={
                    <>
                      {session.coachSessionRemarksV2.map((tag, index) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="text-center mr-1"
                          data-testid={
                            session.coachSessionRemarksV2.length === 1
                              ? sessionDetailValueTestId('teacher', 'session-remarks')
                              : `${sessionDetailValueTestId('teacher', 'session-remarks')}-${index + 1}`
                          }
                        >
                          {formatCoachSessionRemarkV2Label(tag, session)}
                        </Badge>
                      ))}
                      {session.coachSessionRemarksV2.length === 0 && (
                        <span data-testid={sessionDetailValueTestId('teacher', 'session-remarks')}>-</span>
                      )}
                    </>
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormMultiSelect
                        form={form}
                        name="coachSessionRemarksV2"
                        label="Session remark"
                        multipleSelectorProps={{
                          options: buildSessionRemarksV2Options({ selected: coachSessionRemarkV2 }),
                          className: 'w-2/3',
                        }}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Teacher display - Class status"
                  value={
                    session.sessionCompletionStatus ? (
                      <SessionCompletionStatusCell
                        status={session.sessionCompletionStatus}
                        coachSessionRemarksV2={session.coachSessionRemarksV2}
                        session={session}
                        testId={sessionDetailValueTestId('teacher', 'class-status')}
                      />
                    ) : (
                      <span data-testid={sessionDetailValueTestId('teacher', 'class-status')}>-</span>
                    )
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <div className="space-y-2 flex flex-col gap-2 mt-1">
                        <FormLabel className="font-bold">Teacher display - Class status</FormLabel>
                        <div className="w-2/3">
                          <SessionCompletionStatusCell
                            status={session.sessionCompletionStatus}
                            coachSessionRemarksV2={session.coachSessionRemarksV2}
                            session={session}
                          />
                        </div>
                      </div>
                    ),
                  }}
                />

                <InfoKeyValue
                  label="Learning loss"
                  value={
                    <span data-testid={sessionDetailValueTestId('teacher', 'learning-loss')}>
                      {session.learningLoss ? LearningLossLabelMap[session.learningLoss] : '-'}
                    </span>
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormSingleSelect
                        name="learningLoss"
                        label="Learning loss"
                        form={form}
                        options={learningLossSelectableValues}
                        renderOption={getLearningLossLabel}
                        triggerProps={{ className: 'w-2/3' }}
                      />
                    ),
                  }}
                />
              </>
            );
          }}
        </EditableInfoCard>
        <InfoCard headerText="Students">
          <SessionStudentsTable session={session} feedbacks={sessionFeedbacks} isLoading={isLoadingSessionFeedbacks} />
        </InfoCard>
      </InfoPage>
    </div>
  );
}
